import type { LocaleCode } from "../../../shared/src/index";

type UiDictionary = Record<string, string>;

const en: UiDictionary = {
  tagline: "Smart invoicing and compliance platform",
  countryLabel: "Country / region",
  languageLabel: "Language",
  topbarLogin: "Sign in",
  topbarRegister: "Create account",
  topbarManage: "Account",
  close: "Close",
  heroEyebrow: "Southeast Asia invoicing",
  heroTitle: "Draft and review invoices across Southeast Asia.",
  heroSubtitle: "Country-aware guidance, explainable checks, and clean preview pages in one workspace.",
  heroLogin: "Sign in",
  heroRegister: "Create account",
  heroLoggedIn: "Signed in",
  heroCountryBadge: "Selected market: {country}",
  heroRulebookBadge: "Rulebook: {version}",
  heroCountriesBadge: "Supported markets: SG / MY / ID / TH",
  disclaimerTitle: "Operational support",
  disclaimerBody: "Review official rules and professional advice where needed.",
  accountTitle: "Account and workspace",
  accountSubtitleLogin: "Sign in with your email and password.",
  accountSubtitleRegister: "Create your account and open the first workspace for your company.",
  accountSubtitleSignedIn: "Manage your account, switch workspaces, and review your plan details.",
  authDialogTitleLogin: "Sign in",
  authDialogTitleRegister: "Create account",
  authDialogTitleSignedIn: "Account settings",
  loginTab: "Sign in",
  registerTab: "Create account",
  email: "Email",
  password: "Password",
  currentPassword: "Current password",
  newPassword: "New password",
  confirmNewPassword: "Confirm new password",
  displayName: "Display name",
  organizationName: "Company / workspace name",
  organizationCountry: "Workspace country",
  currentOrganization: "Current workspace",
  organizationRole: "Role",
  processing: "Processing...",
  logout: "Sign out",
  changePasswordTitle: "Change password",
  changePasswordBody: "Use your current password, then choose a new one. Other devices will be signed out.",
  changePasswordAction: "Update password",
  loginAction: "Sign in",
  registerAction: "Create account and workspace",
  authRequired: "Please sign in and select a workspace first.",
  authValidationEmailRequired: "Please enter your email first.",
  authValidationEmailInvalid: "Please enter a valid email address.",
  authValidationCurrentPasswordRequired: "Please enter your current password first.",
  authValidationPasswordRequired: "Please enter your password first.",
  authValidationPasswordShort: "Password must contain at least 8 characters.",
  authValidationConfirmPasswordRequired: "Please confirm the new password.",
  authValidationPasswordMismatch: "The new passwords do not match.",
  passwordChangedSuccess: "Password updated. Other devices have been signed out.",
  authValidationDisplayNameRequired: "Please enter a display name.",
  authValidationDisplayNameTooLong: "Display name can contain up to {max} characters.",
  authValidationOrganizationRequired: "Please enter a company or workspace name.",
  authValidationOrganizationTooLong: "Company or workspace name can contain up to {max} characters.",
  serviceUnavailable: "Cloud service is temporarily unavailable. You can still review rules and continue editing the draft.",
  invoiceCore: "Invoice basics",
  invoiceNumber: "Invoice number",
  issueDate: "Issue date",
  issueTime: "Issue time",
  currency: "Currency",
  buyerType: "Buyer type",
  buyerType_business: "Business",
  buyerType_individual: "Individual",
  buyerType_final_consumer: "Final consumer",
  buyerType_government: "Government",
  businessActivity: "Business activity",
  businessActivity_general_services: "General services",
  businessActivity_professional_services: "Professional services",
  businessActivity_it_services: "IT services",
  businessActivity_advertising_services: "Advertising",
  businessActivity_food_and_beverage: "F&B",
  businessActivity_logistics: "Logistics",
  businessActivity_goods: "Goods",
  businessActivity_luxury_goods: "Luxury goods",
  businessActivity_retail: "Retail",
  businessActivity_other: "Other",
  supplyNature: "Supply type",
  supplyNature_domestic: "Domestic",
  supplyNature_export_services: "Export services",
  supplyNature_export_goods: "Export goods",
  supplyNature_exempt: "Exempt / zero-rated",
  supplyNature_reverse_charge: "Reverse charge",
  last12Turnover: "Last 12-month revenue",
  taxRegistered: "Indirect tax registered",
  eInvoiceFlow: "Use Malaysia e-Invoice path",
  seller: "Seller",
  buyer: "Buyer",
  legalName: "Legal name",
  taxNumber: "Tax number",
  phone: "Phone",
  addressLine1: "Address line 1",
  city: "City",
  postalCode: "Postal code",
  projected12Turnover: "Projected next 12-month revenue",
  invoiceItems: "Line items",
  addRow: "Add line",
  description: "Description",
  classCode: "Classification code",
  qty: "Quantity",
  unitPrice: "Unit price",
  taxRate: "Tax rate %",
  netAmount: "Net amount",
  taxAmount: "Tax amount",
  lineTotal: "Line total",
  remove: "Remove",
  subtotal: "Subtotal",
  tax: "Tax",
  grandTotal: "Total due",
  notesLabel: "Notes",
  ocrTextLabel: "Paste OCR text or invoice content",
  uploadImageLabel: "Upload image or PDF to extract invoice data",
  extractDraft: "Extract into draft",
  recalculateTotals: "Recalculate totals",
  uploadTypeError: "Only JPG, PNG, WEBP, and PDF files are supported.",
  uploadSizeError: "One uploaded file can be up to 10MB.",
  uploadSelected: "Selected file: {name} ({size}MB)",
  uploadHelp: "Upload a JPG / PNG / WEBP / PDF file, or paste OCR text directly.",
  extracting: "Extracting invoice content...",
  extractNoDraft: "No usable structured draft was returned.",
  extractFailed: "Extraction failed.",
  checkRunning: "Checking the current draft...",
  checkCompleted: "Compliance check completed.",
  checkFailed: "Compliance check failed.",
  generateRunning: "Generating the preview page...",
  generateCompleted: "Preview page generated.",
  generateFailed: "Preview page generation failed.",
  generateHtml: "Generate preview page",
  riskLabel: "Risk",
  rulebookLabel: "Rulebook",
  runServerCheck: "Check current draft",
  thresholdWatch: "Business watchlist",
  previewTitle: "Customer preview page",
  previewEmpty: "Generate the preview page to review the customer-facing output.",
  sourcesTitle: "Official rule sources",
  sourcesSubtitle: "Current official sources for {country}, rulebook {version}.",
  updatedOn: "Updated",
  accessedOn: "Accessed",
  notAvailable: "n/a",
  recentRuns: "Check snapshots",
  noRuns: "No saved checks yet.",
  statusQueued: "Queued",
  statusProcessing: "Processing",
  statusCompleted: "Completed",
  statusFailed: "Failed",
  triggerSync: "Realtime",
  reverseCharge: "Reverse-charge note"
};

const zhCN: UiDictionary = {
  tagline: "智能开票与合规平台",
  countryLabel: "国家 / 地区",
  languageLabel: "语言",
  topbarLogin: "登录",
  topbarRegister: "注册",
  topbarManage: "账户",
  close: "关闭",
  heroEyebrow: "东南亚发票与合规",
  heroTitle: "面向东南亚市场的发票起草与复核。",
  heroSubtitle: "在一个工作台里完成本地化引导、可解释检查和客户预览。",
  heroLogin: "登录",
  heroRegister: "创建账户",
  heroLoggedIn: "已登录",
  heroCountryBadge: "当前市场：{country}",
  heroRulebookBadge: "规则版本：{version}",
  heroCountriesBadge: "支持市场：SG / MY / ID / TH",
  disclaimerTitle: "业务辅助",
  disclaimerBody: "必要时仍请结合官方规则与专业意见复核。",
  accountTitle: "账户与工作空间",
  accountSubtitleLogin: "使用邮箱和密码登录。",
  accountSubtitleRegister: "创建你的账户，并为公司开通第一个工作空间。",
  accountSubtitleSignedIn: "管理账户、切换工作空间，并查看当前套餐信息。",
  authDialogTitleLogin: "登录",
  authDialogTitleRegister: "创建账户",
  authDialogTitleSignedIn: "账户设置",
  loginTab: "登录",
  registerTab: "创建账户",
  email: "邮箱",
  password: "密码",
  currentPassword: "当前密码",
  newPassword: "新密码",
  confirmNewPassword: "确认新密码",
  displayName: "显示名称",
  organizationName: "公司 / 工作空间名称",
  organizationCountry: "工作空间国家",
  currentOrganization: "当前工作空间",
  organizationRole: "角色",
  processing: "处理中...",
  logout: "退出登录",
  changePasswordTitle: "修改密码",
  changePasswordBody: "请先输入当前密码，再设置一个新密码。其他设备上的登录会被自动退出。",
  changePasswordAction: "更新密码",
  loginAction: "登录",
  registerAction: "创建账户并开通工作空间",
  authRequired: "请先登录并选择工作空间。",
  authValidationEmailRequired: "请先输入邮箱。",
  authValidationEmailInvalid: "请输入有效的邮箱地址。",
  authValidationCurrentPasswordRequired: "请先输入当前密码。",
  authValidationPasswordRequired: "请先输入密码。",
  authValidationPasswordShort: "密码至少需要 8 位。",
  authValidationConfirmPasswordRequired: "请确认新密码。",
  authValidationPasswordMismatch: "两次输入的新密码不一致。",
  passwordChangedSuccess: "密码已更新，其他设备上的登录已退出。",
  authValidationDisplayNameRequired: "请输入显示名称。",
  authValidationDisplayNameTooLong: "显示名称最多可输入 {max} 个字符。",
  authValidationOrganizationRequired: "请输入公司或工作空间名称。",
  authValidationOrganizationTooLong: "公司或工作空间名称最多可输入 {max} 个字符。",
  serviceUnavailable: "云端服务暂时不可用，你仍可继续查看规则并编辑草稿。",
  invoiceCore: "基础信息",
  invoiceNumber: "发票编号",
  issueDate: "开票日期",
  issueTime: "开票时间",
  currency: "币种",
  buyerType: "买方类型",
  buyerType_business: "企业",
  buyerType_individual: "个人",
  buyerType_final_consumer: "最终消费者",
  buyerType_government: "政府机构",
  businessActivity: "业务类型",
  businessActivity_general_services: "通用服务",
  businessActivity_professional_services: "专业服务",
  businessActivity_it_services: "IT 服务",
  businessActivity_advertising_services: "广告服务",
  businessActivity_food_and_beverage: "餐饮",
  businessActivity_logistics: "物流",
  businessActivity_goods: "货物销售",
  businessActivity_luxury_goods: "奢侈品",
  businessActivity_retail: "零售",
  businessActivity_other: "其他",
  supplyNature: "交易性质",
  supplyNature_domestic: "境内交易",
  supplyNature_export_services: "服务出口",
  supplyNature_export_goods: "货物出口",
  supplyNature_exempt: "免税 / 零税率",
  supplyNature_reverse_charge: "反向征税",
  last12Turnover: "过去 12 个月营业额",
  taxRegistered: "已完成间接税注册",
  eInvoiceFlow: "启用马来西亚 e-Invoice 路径",
  seller: "卖方",
  buyer: "买方",
  legalName: "法定名称",
  taxNumber: "税号",
  phone: "电话",
  addressLine1: "地址第一行",
  city: "城市",
  postalCode: "邮编",
  projected12Turnover: "未来 12 个月预计营业额",
  invoiceItems: "行项目",
  addRow: "新增行",
  description: "描述",
  classCode: "分类编码",
  qty: "数量",
  unitPrice: "单价",
  taxRate: "税率 %",
  netAmount: "未税金额",
  taxAmount: "税额",
  lineTotal: "行总额",
  remove: "删除",
  subtotal: "小计",
  tax: "税额",
  grandTotal: "应付总额",
  notesLabel: "备注",
  ocrTextLabel: "粘贴 OCR 文本或发票内容",
  uploadImageLabel: "上传图片或 PDF，识别发票内容",
  extractDraft: "抽取到草稿",
  recalculateTotals: "重新计算金额",
  uploadTypeError: "仅支持 JPG、PNG、WEBP 和 PDF 文件。",
  uploadSizeError: "单个上传文件最大为 10MB。",
  uploadSelected: "已选择文件：{name}（{size}MB）",
  uploadHelp: "支持上传 JPG / PNG / WEBP / PDF，或直接粘贴 OCR 文本。",
  extracting: "正在抽取发票内容...",
  extractNoDraft: "没有返回可用的结构化草稿。",
  extractFailed: "抽取失败。",
  checkRunning: "正在检查当前草稿...",
  checkCompleted: "合规检查已完成。",
  checkFailed: "合规检查失败。",
  generateRunning: "正在生成预览页...",
  generateCompleted: "预览页已生成。",
  generateFailed: "预览页生成失败。",
  generateHtml: "生成预览页",
  riskLabel: "风险",
  rulebookLabel: "规则版本",
  runServerCheck: "检查当前草稿",
  thresholdWatch: "业务提醒",
  previewTitle: "客户预览页",
  previewEmpty: "生成预览页后，可在这里查看面向客户的最终展示效果。",
  sourcesTitle: "官方规则来源",
  sourcesSubtitle: "{country} 当前使用的官方来源，规则版本 {version}。",
  updatedOn: "更新时间",
  accessedOn: "核验日期",
  notAvailable: "暂无",
  recentRuns: "检查快照",
  noRuns: "还没有保存的检查记录。",
  statusQueued: "已排队",
  statusProcessing: "处理中",
  statusCompleted: "已完成",
  statusFailed: "失败",
  triggerSync: "实时",
  reverseCharge: "反向征税说明"
};

const msMY: UiDictionary = {
  tagline: "Platform invois pintar dan pematuhan",
  countryLabel: "Negara / wilayah",
  languageLabel: "Bahasa",
  topbarLogin: "Log masuk",
  topbarRegister: "Buka akaun",
  topbarManage: "Akaun",
  close: "Tutup",
  heroEyebrow: "Invois Asia Tenggara",
  heroTitle: "Draf dan semak invois merentas Asia Tenggara.",
  heroSubtitle: "Panduan mengikut negara, semakan yang boleh dijelaskan, dan pratonton pelanggan dalam satu ruang kerja.",
  heroLogin: "Log masuk",
  heroRegister: "Buka akaun",
  heroLoggedIn: "Sudah log masuk",
  heroCountryBadge: "Pasaran semasa: {country}",
  heroRulebookBadge: "Versi peraturan: {version}",
  heroCountriesBadge: "Pasaran disokong: SG / MY / ID / TH",
  disclaimerTitle: "Sokongan operasi",
  disclaimerBody: "Rujuk peraturan rasmi dan nasihat profesional apabila perlu.",
  accountTitle: "Akaun dan ruang kerja",
  accountSubtitleLogin: "Log masuk dengan e-mel dan kata laluan anda.",
  accountSubtitleRegister: "Buka akaun anda dan cipta ruang kerja pertama untuk syarikat anda.",
  accountSubtitleSignedIn: "Urus akaun, tukar ruang kerja, dan semak butiran pelan anda.",
  authDialogTitleLogin: "Log masuk",
  authDialogTitleRegister: "Buka akaun",
  authDialogTitleSignedIn: "Tetapan akaun",
  loginTab: "Log masuk",
  registerTab: "Buka akaun",
  email: "E-mel",
  password: "Kata laluan",
  currentPassword: "Kata laluan semasa",
  newPassword: "Kata laluan baharu",
  confirmNewPassword: "Sahkan kata laluan baharu",
  displayName: "Nama paparan",
  organizationName: "Nama syarikat / ruang kerja",
  organizationCountry: "Negara ruang kerja",
  currentOrganization: "Ruang kerja semasa",
  organizationRole: "Peranan",
  processing: "Sedang diproses...",
  logout: "Log keluar",
  changePasswordTitle: "Tukar kata laluan",
  changePasswordBody: "Masukkan kata laluan semasa, kemudian tetapkan yang baharu. Peranti lain akan dilog keluar.",
  changePasswordAction: "Kemas kini kata laluan",
  loginAction: "Log masuk",
  registerAction: "Buka akaun dan ruang kerja",
  authRequired: "Sila log masuk dan pilih ruang kerja terlebih dahulu.",
  authValidationEmailRequired: "Sila masukkan e-mel terlebih dahulu.",
  authValidationEmailInvalid: "Sila masukkan alamat e-mel yang sah.",
  authValidationCurrentPasswordRequired: "Sila masukkan kata laluan semasa terlebih dahulu.",
  authValidationPasswordRequired: "Sila masukkan kata laluan terlebih dahulu.",
  authValidationPasswordShort: "Kata laluan mesti sekurang-kurangnya 8 aksara.",
  authValidationConfirmPasswordRequired: "Sila sahkan kata laluan baharu.",
  authValidationPasswordMismatch: "Kata laluan baharu tidak sepadan.",
  passwordChangedSuccess: "Kata laluan telah dikemas kini. Peranti lain telah dilog keluar.",
  authValidationDisplayNameRequired: "Sila masukkan nama paparan.",
  authValidationDisplayNameTooLong: "Nama paparan boleh mengandungi sehingga {max} aksara.",
  authValidationOrganizationRequired: "Sila masukkan nama syarikat atau ruang kerja.",
  authValidationOrganizationTooLong: "Nama syarikat atau ruang kerja boleh mengandungi sehingga {max} aksara.",
  serviceUnavailable: "Perkhidmatan awan buat sementara tidak tersedia. Anda masih boleh menyemak peraturan dan terus mengedit draf.",
  invoiceCore: "Maklumat asas",
  invoiceNumber: "Nombor invois",
  issueDate: "Tarikh invois",
  issueTime: "Masa invois",
  currency: "Mata wang",
  buyerType: "Jenis pembeli",
  buyerType_business: "Perniagaan",
  buyerType_individual: "Individu",
  buyerType_final_consumer: "Pengguna akhir",
  buyerType_government: "Kerajaan",
  businessActivity: "Aktiviti perniagaan",
  businessActivity_general_services: "Perkhidmatan umum",
  businessActivity_professional_services: "Perkhidmatan profesional",
  businessActivity_it_services: "Perkhidmatan IT",
  businessActivity_advertising_services: "Pengiklanan",
  businessActivity_food_and_beverage: "Makanan & minuman",
  businessActivity_logistics: "Logistik",
  businessActivity_goods: "Barangan",
  businessActivity_luxury_goods: "Barangan mewah",
  businessActivity_retail: "Runcit",
  businessActivity_other: "Lain-lain",
  supplyNature: "Jenis transaksi",
  supplyNature_domestic: "Domestik",
  supplyNature_export_services: "Eksport perkhidmatan",
  supplyNature_export_goods: "Eksport barangan",
  supplyNature_exempt: "Dikecualikan / kadar sifar",
  supplyNature_reverse_charge: "Caj songsang",
  last12Turnover: "Hasil 12 bulan lepas",
  taxRegistered: "Sudah berdaftar cukai tidak langsung",
  eInvoiceFlow: "Gunakan laluan e-Invoice Malaysia",
  seller: "Penjual",
  buyer: "Pembeli",
  legalName: "Nama sah",
  taxNumber: "Nombor cukai",
  phone: "Telefon",
  addressLine1: "Baris alamat pertama",
  city: "Bandar",
  postalCode: "Poskod",
  projected12Turnover: "Unjuran hasil 12 bulan akan datang",
  invoiceItems: "Item invois",
  addRow: "Tambah baris",
  description: "Keterangan",
  classCode: "Kod klasifikasi",
  qty: "Kuantiti",
  unitPrice: "Harga seunit",
  taxRate: "Kadar cukai %",
  netAmount: "Amaun bersih",
  taxAmount: "Amaun cukai",
  lineTotal: "Jumlah baris",
  remove: "Buang",
  subtotal: "Subjumlah",
  tax: "Cukai",
  grandTotal: "Jumlah perlu dibayar",
  notesLabel: "Catatan",
  ocrTextLabel: "Tampal teks OCR atau kandungan invois",
  uploadImageLabel: "Muat naik imej atau PDF untuk mengekstrak data invois",
  extractDraft: "Ekstrak ke draf",
  recalculateTotals: "Kira semula jumlah",
  uploadTypeError: "Hanya fail JPG, PNG, WEBP, dan PDF disokong.",
  uploadSizeError: "Satu fail muat naik boleh sehingga 10MB.",
  uploadSelected: "Fail dipilih: {name} ({size}MB)",
  uploadHelp: "Muat naik fail JPG / PNG / WEBP / PDF, atau tampal teks OCR secara terus.",
  extracting: "Sedang mengekstrak kandungan invois...",
  extractNoDraft: "Tiada draf berstruktur yang boleh digunakan dipulangkan.",
  extractFailed: "Pengekstrakan gagal.",
  checkRunning: "Sedang menyemak draf semasa...",
  checkCompleted: "Semakan pematuhan selesai.",
  checkFailed: "Semakan pematuhan gagal.",
  generateRunning: "Sedang menjana halaman pratonton...",
  generateCompleted: "Halaman pratonton dijana.",
  generateFailed: "Penjanaan halaman pratonton gagal.",
  generateHtml: "Jana halaman pratonton",
  riskLabel: "Risiko",
  rulebookLabel: "Versi peraturan",
  runServerCheck: "Semak draf semasa",
  thresholdWatch: "Peringatan perniagaan",
  previewTitle: "Halaman pratonton pelanggan",
  previewEmpty: "Jana halaman pratonton untuk menyemak paparan akhir yang akan dilihat pelanggan.",
  sourcesTitle: "Sumber peraturan rasmi",
  sourcesSubtitle: "Sumber rasmi semasa untuk {country}, versi peraturan {version}.",
  updatedOn: "Dikemas kini",
  accessedOn: "Diakses",
  notAvailable: "tiada",
  recentRuns: "Rekod semakan",
  noRuns: "Belum ada semakan yang disimpan.",
  statusQueued: "Beratur",
  statusProcessing: "Sedang diproses",
  statusCompleted: "Selesai",
  statusFailed: "Gagal",
  triggerSync: "Masa nyata",
  reverseCharge: "Nota caj songsang"
};

const idID: UiDictionary = {
  tagline: "Platform invoice pintar dan kepatuhan",
  countryLabel: "Negara / wilayah",
  languageLabel: "Bahasa",
  topbarLogin: "Masuk",
  topbarRegister: "Buat akun",
  topbarManage: "Akun",
  close: "Tutup",
  heroEyebrow: "Invoice Asia Tenggara",
  heroTitle: "Draft dan tinjau invoice di Asia Tenggara.",
  heroSubtitle: "Panduan per negara, pemeriksaan yang mudah dijelaskan, dan pratinjau pelanggan dalam satu workspace.",
  heroLogin: "Masuk",
  heroRegister: "Buat akun",
  heroLoggedIn: "Sudah masuk",
  heroCountryBadge: "Pasar aktif: {country}",
  heroRulebookBadge: "Versi aturan: {version}",
  heroCountriesBadge: "Pasar yang didukung: SG / MY / ID / TH",
  disclaimerTitle: "Bantuan operasional",
  disclaimerBody: "Tetap rujuk aturan resmi dan nasihat profesional bila diperlukan.",
  accountTitle: "Akun dan workspace",
  accountSubtitleLogin: "Masuk dengan email dan kata sandi Anda.",
  accountSubtitleRegister: "Buat akun Anda dan buka workspace pertama untuk perusahaan Anda.",
  accountSubtitleSignedIn: "Kelola akun, ganti workspace, dan tinjau detail paket Anda.",
  authDialogTitleLogin: "Masuk",
  authDialogTitleRegister: "Buat akun",
  authDialogTitleSignedIn: "Pengaturan akun",
  loginTab: "Masuk",
  registerTab: "Buat akun",
  email: "Email",
  password: "Kata sandi",
  currentPassword: "Kata sandi saat ini",
  newPassword: "Kata sandi baru",
  confirmNewPassword: "Konfirmasi kata sandi baru",
  displayName: "Nama tampilan",
  organizationName: "Nama perusahaan / workspace",
  organizationCountry: "Negara workspace",
  currentOrganization: "Workspace aktif",
  organizationRole: "Peran",
  processing: "Sedang diproses...",
  logout: "Keluar",
  changePasswordTitle: "Ubah kata sandi",
  changePasswordBody: "Masukkan kata sandi saat ini lalu pilih yang baru. Perangkat lain akan otomatis keluar.",
  changePasswordAction: "Perbarui kata sandi",
  loginAction: "Masuk",
  registerAction: "Buat akun dan workspace",
  authRequired: "Silakan masuk dan pilih workspace terlebih dahulu.",
  authValidationEmailRequired: "Silakan masukkan email terlebih dahulu.",
  authValidationEmailInvalid: "Silakan masukkan alamat email yang valid.",
  authValidationCurrentPasswordRequired: "Silakan masukkan kata sandi saat ini terlebih dahulu.",
  authValidationPasswordRequired: "Silakan masukkan kata sandi terlebih dahulu.",
  authValidationPasswordShort: "Kata sandi harus minimal 8 karakter.",
  authValidationConfirmPasswordRequired: "Silakan konfirmasi kata sandi baru.",
  authValidationPasswordMismatch: "Kata sandi baru tidak sama.",
  passwordChangedSuccess: "Kata sandi berhasil diperbarui. Perangkat lain sudah keluar.",
  authValidationDisplayNameRequired: "Silakan masukkan nama tampilan.",
  authValidationDisplayNameTooLong: "Nama tampilan dapat berisi hingga {max} karakter.",
  authValidationOrganizationRequired: "Silakan masukkan nama perusahaan atau workspace.",
  authValidationOrganizationTooLong: "Nama perusahaan atau workspace dapat berisi hingga {max} karakter.",
  serviceUnavailable: "Layanan cloud sementara tidak tersedia. Anda masih bisa meninjau aturan dan melanjutkan pengeditan draft.",
  invoiceCore: "Informasi dasar",
  invoiceNumber: "Nomor invoice",
  issueDate: "Tanggal invoice",
  issueTime: "Waktu invoice",
  currency: "Mata uang",
  buyerType: "Jenis pembeli",
  buyerType_business: "Bisnis",
  buyerType_individual: "Pribadi",
  buyerType_final_consumer: "Konsumen akhir",
  buyerType_government: "Pemerintah",
  businessActivity: "Aktivitas bisnis",
  businessActivity_general_services: "Layanan umum",
  businessActivity_professional_services: "Layanan profesional",
  businessActivity_it_services: "Layanan TI",
  businessActivity_advertising_services: "Periklanan",
  businessActivity_food_and_beverage: "Makanan & minuman",
  businessActivity_logistics: "Logistik",
  businessActivity_goods: "Barang",
  businessActivity_luxury_goods: "Barang mewah",
  businessActivity_retail: "Ritel",
  businessActivity_other: "Lainnya",
  supplyNature: "Jenis transaksi",
  supplyNature_domestic: "Domestik",
  supplyNature_export_services: "Ekspor jasa",
  supplyNature_export_goods: "Ekspor barang",
  supplyNature_exempt: "Dikecualikan / tarif 0%",
  supplyNature_reverse_charge: "Reverse charge",
  last12Turnover: "Pendapatan 12 bulan terakhir",
  taxRegistered: "Sudah terdaftar pajak tidak langsung",
  eInvoiceFlow: "Gunakan jalur e-Invoice Malaysia",
  seller: "Penjual",
  buyer: "Pembeli",
  legalName: "Nama resmi",
  taxNumber: "Nomor pajak",
  phone: "Telepon",
  addressLine1: "Baris alamat pertama",
  city: "Kota",
  postalCode: "Kode pos",
  projected12Turnover: "Proyeksi pendapatan 12 bulan berikutnya",
  invoiceItems: "Item invoice",
  addRow: "Tambah baris",
  description: "Deskripsi",
  classCode: "Kode klasifikasi",
  qty: "Kuantitas",
  unitPrice: "Harga satuan",
  taxRate: "Tarif pajak %",
  netAmount: "Nilai bersih",
  taxAmount: "Nilai pajak",
  lineTotal: "Total baris",
  remove: "Hapus",
  subtotal: "Subtotal",
  tax: "Pajak",
  grandTotal: "Total tagihan",
  notesLabel: "Catatan",
  ocrTextLabel: "Tempel teks OCR atau isi invoice",
  uploadImageLabel: "Unggah gambar atau PDF untuk mengekstrak data invoice",
  extractDraft: "Ekstrak ke draft",
  recalculateTotals: "Hitung ulang total",
  uploadTypeError: "Hanya file JPG, PNG, WEBP, dan PDF yang didukung.",
  uploadSizeError: "Satu file unggahan maksimal 10MB.",
  uploadSelected: "File terpilih: {name} ({size}MB)",
  uploadHelp: "Unggah file JPG / PNG / WEBP / PDF, atau tempel teks OCR secara langsung.",
  extracting: "Sedang mengekstrak isi invoice...",
  extractNoDraft: "Tidak ada draft terstruktur yang dapat dipakai.",
  extractFailed: "Ekstraksi gagal.",
  checkRunning: "Sedang memeriksa draft saat ini...",
  checkCompleted: "Pemeriksaan kepatuhan selesai.",
  checkFailed: "Pemeriksaan kepatuhan gagal.",
  generateRunning: "Sedang membuat halaman pratinjau...",
  generateCompleted: "Halaman pratinjau berhasil dibuat.",
  generateFailed: "Pembuatan halaman pratinjau gagal.",
  generateHtml: "Buat halaman pratinjau",
  riskLabel: "Risiko",
  rulebookLabel: "Versi aturan",
  runServerCheck: "Periksa draft saat ini",
  thresholdWatch: "Pengingat bisnis",
  previewTitle: "Halaman pratinjau pelanggan",
  previewEmpty: "Buat halaman pratinjau untuk meninjau tampilan akhir yang akan dilihat pelanggan.",
  sourcesTitle: "Sumber aturan resmi",
  sourcesSubtitle: "Sumber resmi saat ini untuk {country}, versi aturan {version}.",
  updatedOn: "Diperbarui",
  accessedOn: "Diakses",
  notAvailable: "tidak ada",
  recentRuns: "Snapshot pemeriksaan",
  noRuns: "Belum ada pemeriksaan tersimpan.",
  statusQueued: "Antri",
  statusProcessing: "Diproses",
  statusCompleted: "Selesai",
  statusFailed: "Gagal",
  triggerSync: "Realtime",
  reverseCharge: "Catatan reverse charge"
};

const thTH: UiDictionary = {
  tagline: "แพลตฟอร์มออกเอกสารและตรวจสอบความสอดคล้อง",
  countryLabel: "ประเทศ / ภูมิภาค",
  languageLabel: "ภาษา",
  topbarLogin: "เข้าสู่ระบบ",
  topbarRegister: "สมัครใช้งาน",
  topbarManage: "บัญชี",
  close: "ปิด",
  heroEyebrow: "เอกสารในเอเชียตะวันออกเฉียงใต้",
  heroTitle: "ร่างและตรวจเอกสารสำหรับตลาดเอเชียตะวันออกเฉียงใต้",
  heroSubtitle: "คำแนะนำตามประเทศ การตรวจที่อธิบายได้ และพรีวิวสำหรับลูกค้าในเวิร์กสเปซเดียว",
  heroLogin: "เข้าสู่ระบบ",
  heroRegister: "สร้างบัญชี",
  heroLoggedIn: "เข้าสู่ระบบแล้ว",
  heroCountryBadge: "ตลาดที่เลือก: {country}",
  heroRulebookBadge: "เวอร์ชันกฎ: {version}",
  heroCountriesBadge: "ตลาดที่รองรับ: SG / MY / ID / TH",
  disclaimerTitle: "ตัวช่วยด้านงานปฏิบัติการ",
  disclaimerBody: "ควรตรวจทานกับกฎทางการและคำแนะนำจากผู้เชี่ยวชาญเมื่อจำเป็น",
  accountTitle: "บัญชีและเวิร์กสเปซ",
  accountSubtitleLogin: "เข้าสู่ระบบด้วยอีเมลและรหัสผ่านของคุณ",
  accountSubtitleRegister: "สร้างบัญชีและเปิดเวิร์กสเปซแรกสำหรับบริษัทของคุณ",
  accountSubtitleSignedIn: "จัดการบัญชี สลับเวิร์กสเปซ และดูรายละเอียดแพ็กเกจปัจจุบัน",
  authDialogTitleLogin: "เข้าสู่ระบบ",
  authDialogTitleRegister: "สร้างบัญชี",
  authDialogTitleSignedIn: "ตั้งค่าบัญชี",
  loginTab: "เข้าสู่ระบบ",
  registerTab: "สร้างบัญชี",
  email: "อีเมล",
  password: "รหัสผ่าน",
  currentPassword: "รหัสผ่านปัจจุบัน",
  newPassword: "รหัสผ่านใหม่",
  confirmNewPassword: "ยืนยันรหัสผ่านใหม่",
  displayName: "ชื่อที่แสดง",
  organizationName: "ชื่อบริษัท / เวิร์กสเปซ",
  organizationCountry: "ประเทศของเวิร์กสเปซ",
  currentOrganization: "เวิร์กสเปซปัจจุบัน",
  organizationRole: "บทบาท",
  processing: "กำลังประมวลผล...",
  logout: "ออกจากระบบ",
  changePasswordTitle: "เปลี่ยนรหัสผ่าน",
  changePasswordBody: "กรอกรหัสผ่านปัจจุบัน แล้วตั้งรหัสผ่านใหม่ อุปกรณ์อื่นจะถูกออกจากระบบโดยอัตโนมัติ",
  changePasswordAction: "อัปเดตรหัสผ่าน",
  loginAction: "เข้าสู่ระบบ",
  registerAction: "สร้างบัญชีและเวิร์กสเปซ",
  authRequired: "กรุณาเข้าสู่ระบบและเลือกเวิร์กสเปซก่อน",
  authValidationEmailRequired: "กรุณากรอกอีเมลก่อน",
  authValidationEmailInvalid: "กรุณากรอกอีเมลที่ถูกต้อง",
  authValidationCurrentPasswordRequired: "กรุณากรอกรหัสผ่านปัจจุบันก่อน",
  authValidationPasswordRequired: "กรุณากรอกรหัสผ่านก่อน",
  authValidationPasswordShort: "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร",
  authValidationConfirmPasswordRequired: "กรุณายืนยันรหัสผ่านใหม่",
  authValidationPasswordMismatch: "รหัสผ่านใหม่ทั้งสองช่องไม่ตรงกัน",
  passwordChangedSuccess: "อัปเดตรหัสผ่านแล้ว และระบบได้ออกจากระบบอุปกรณ์อื่นเรียบร้อย",
  authValidationDisplayNameRequired: "กรุณากรอกชื่อที่แสดง",
  authValidationDisplayNameTooLong: "ชื่อที่แสดงยาวได้ไม่เกิน {max} ตัวอักษร",
  authValidationOrganizationRequired: "กรุณากรอกชื่อบริษัทหรือเวิร์กสเปซ",
  authValidationOrganizationTooLong: "ชื่อบริษัทหรือเวิร์กสเปซยาวได้ไม่เกิน {max} ตัวอักษร",
  serviceUnavailable: "บริการคลาวด์ใช้งานไม่ได้ชั่วคราว คุณยังตรวจดูกฎและแก้ไขร่างต่อได้",
  invoiceCore: "ข้อมูลพื้นฐาน",
  invoiceNumber: "เลขเอกสาร",
  issueDate: "วันที่ออกเอกสาร",
  issueTime: "เวลาออกเอกสาร",
  currency: "สกุลเงิน",
  buyerType: "ประเภทผู้ซื้อ",
  buyerType_business: "นิติบุคคล",
  buyerType_individual: "บุคคลธรรมดา",
  buyerType_final_consumer: "ผู้บริโภคปลายทาง",
  buyerType_government: "หน่วยงานรัฐ",
  businessActivity: "ประเภทธุรกิจ",
  businessActivity_general_services: "บริการทั่วไป",
  businessActivity_professional_services: "บริการวิชาชีพ",
  businessActivity_it_services: "บริการไอที",
  businessActivity_advertising_services: "โฆษณา",
  businessActivity_food_and_beverage: "อาหารและเครื่องดื่ม",
  businessActivity_logistics: "โลจิสติกส์",
  businessActivity_goods: "สินค้า",
  businessActivity_luxury_goods: "สินค้าฟุ่มเฟือย",
  businessActivity_retail: "ค้าปลีก",
  businessActivity_other: "อื่น ๆ",
  supplyNature: "ลักษณะรายการ",
  supplyNature_domestic: "ในประเทศ",
  supplyNature_export_services: "ส่งออกบริการ",
  supplyNature_export_goods: "ส่งออกสินค้า",
  supplyNature_exempt: "ยกเว้น / อัตรา 0%",
  supplyNature_reverse_charge: "Reverse charge",
  last12Turnover: "รายได้ 12 เดือนย้อนหลัง",
  taxRegistered: "จดทะเบียนภาษีทางอ้อมแล้ว",
  eInvoiceFlow: "ใช้เส้นทาง e-Invoice ของมาเลเซีย",
  seller: "ผู้ขาย",
  buyer: "ผู้ซื้อ",
  legalName: "ชื่อตามกฎหมาย",
  taxNumber: "เลขภาษี",
  phone: "โทรศัพท์",
  addressLine1: "ที่อยู่บรรทัดแรก",
  city: "เมือง",
  postalCode: "รหัสไปรษณีย์",
  projected12Turnover: "ประมาณการรายได้ 12 เดือนข้างหน้า",
  invoiceItems: "รายการสินค้า / บริการ",
  addRow: "เพิ่มรายการ",
  description: "คำอธิบาย",
  classCode: "รหัสจัดประเภท",
  qty: "จำนวน",
  unitPrice: "ราคาต่อหน่วย",
  taxRate: "อัตราภาษี %",
  netAmount: "ยอดก่อนภาษี",
  taxAmount: "ยอดภาษี",
  lineTotal: "ยอดรวมต่อรายการ",
  remove: "ลบ",
  subtotal: "ยอดย่อย",
  tax: "ภาษี",
  grandTotal: "ยอดที่ต้องชำระ",
  notesLabel: "หมายเหตุ",
  ocrTextLabel: "วางข้อความ OCR หรือเนื้อหาเอกสาร",
  uploadImageLabel: "อัปโหลดรูปภาพหรือ PDF เพื่อดึงข้อมูลเอกสาร",
  extractDraft: "ดึงลงร่าง",
  recalculateTotals: "คำนวณยอดใหม่",
  uploadTypeError: "รองรับเฉพาะไฟล์ JPG, PNG, WEBP และ PDF เท่านั้น",
  uploadSizeError: "ไฟล์อัปโหลดหนึ่งไฟล์มีขนาดได้สูงสุด 10MB",
  uploadSelected: "ไฟล์ที่เลือก: {name} ({size}MB)",
  uploadHelp: "อัปโหลดไฟล์ JPG / PNG / WEBP / PDF หรือวางข้อความ OCR โดยตรง",
  extracting: "กำลังดึงข้อมูลเอกสาร...",
  extractNoDraft: "ไม่มีร่างแบบมีโครงสร้างที่ใช้งานได้ถูกส่งกลับมา",
  extractFailed: "ดึงข้อมูลไม่สำเร็จ",
  checkRunning: "กำลังตรวจร่างปัจจุบัน...",
  checkCompleted: "ตรวจความสอดคล้องเสร็จแล้ว",
  checkFailed: "ตรวจความสอดคล้องไม่สำเร็จ",
  generateRunning: "กำลังสร้างหน้าพรีวิว...",
  generateCompleted: "สร้างหน้าพรีวิวแล้ว",
  generateFailed: "สร้างหน้าพรีวิวไม่สำเร็จ",
  generateHtml: "สร้างหน้าพรีวิว",
  riskLabel: "ความเสี่ยง",
  rulebookLabel: "เวอร์ชันกฎ",
  runServerCheck: "ตรวจร่างปัจจุบัน",
  thresholdWatch: "รายการเฝ้าระวังทางธุรกิจ",
  previewTitle: "หน้าพรีวิวสำหรับลูกค้า",
  previewEmpty: "สร้างหน้าพรีวิวเพื่อดูผลลัพธ์สุดท้ายที่ลูกค้าจะเห็น",
  sourcesTitle: "แหล่งอ้างอิงกฎทางการ",
  sourcesSubtitle: "แหล่งอ้างอิงทางการของ {country} ภายใต้เวอร์ชันกฎ {version}",
  updatedOn: "อัปเดต",
  accessedOn: "ตรวจทานเมื่อ",
  notAvailable: "ไม่มี",
  recentRuns: "สแนปชอตการตรวจ",
  noRuns: "ยังไม่มีผลการตรวจที่บันทึกไว้",
  statusQueued: "เข้าคิวแล้ว",
  statusProcessing: "กำลังประมวลผล",
  statusCompleted: "เสร็จแล้ว",
  statusFailed: "ล้มเหลว",
  triggerSync: "เรียลไทม์",
  reverseCharge: "หมายเหตุ Reverse charge"
};

const dictionaries: Record<LocaleCode, UiDictionary> = {
  en,
  "zh-CN": zhCN,
  "ms-MY": msMY,
  "id-ID": idID,
  "th-TH": thTH
};

export const getLocaleLabel = (locale: LocaleCode): string =>
  ({
    en: "English",
    "zh-CN": "简体中文",
    "ms-MY": "Bahasa Melayu",
    "id-ID": "Bahasa Indonesia",
    "th-TH": "ไทย"
  })[locale];

export const ui = (locale: LocaleCode, key: string, params: Record<string, string | number> = {}): string => {
  const template = dictionaries[locale]?.[key] ?? en[key] ?? key;
  return template.replace(/\{(\w+)\}/g, (_, param: string) => String(params[param] ?? ""));
};
